/**
 * BL-2282 (PRJ-89) — the one resolver every staff surface names a closed
 * auction through: the house's Closed label at stage 0, its stage names above.
 */

/**
 * A house's closing-stage labels, typed structurally so core never imports the
 * core-admin DTO (`TenantCloseStageLabels`, ADR 054 — Package Layers).
 * `stages[n - 1]` names stage n; `closedLabel: null` means the default word.
 */
export interface CloseStageLabelSource {
  /** The house's word for Closed; `null` means {@link DEFAULT_CLOSED_LABEL}. */
  closedLabel: string | null;
  /** The named stages, in order, contiguous from stage 1. */
  stages: readonly string[];
}

/** The word staff read for a closed auction when the house set no label of its own. */
export const DEFAULT_CLOSED_LABEL = 'Closed';

/**
 * BL-2325 — the labels of a house that configured nothing: the default Closed
 * word and no named stages. The one fallback every reader uses when a house's
 * labels are absent (not loaded yet, or the house is missing or archived).
 * Frozen, so no reader can mutate the shared value.
 */
export const UNCONFIGURED_CLOSE_STAGE_LABELS: Readonly<CloseStageLabelSource> = Object.freeze({
  closedLabel: null,
  stages: Object.freeze([] as string[]),
});

/**
 * The in-sentence default. Today's sentences read the adjective ("This auction
 * is closed."), so a house that set no label keeps exactly that wording (AC 5).
 */
const DEFAULT_CLOSED_LABEL_IN_SENTENCE = 'closed';

/**
 * The house's name for its Closed status, as a pill, menu row or filter option shows it.
 *
 * @param labels - The house's labels (only `closedLabel` is read).
 * @returns The house's label, or "Closed" when it set none.
 */
export function houseClosedLabel(labels: Pick<CloseStageLabelSource, 'closedLabel'>): string {
  return labels.closedLabel ?? DEFAULT_CLOSED_LABEL;
}

/**
 * The house's Closed label as it reads inside a sentence ("This auction is ___.").
 * A house's own label is used verbatim; the default is the lowercase adjective,
 * so a house that set no label reads today's sentences byte for byte (AC 5).
 *
 * @param labels - The house's labels (only `closedLabel` is read).
 * @returns The house's label verbatim, or "closed" when it set none.
 */
export function closedLabelInSentence(labels: Pick<CloseStageLabelSource, 'closedLabel'>): string {
  return labels.closedLabel ?? DEFAULT_CLOSED_LABEL_IN_SENTENCE;
}

/**
 * The label for a closed auction at a closing stage: the house's Closed label
 * at stage 0, the house's name for stage n above it, and "Closing stage n" for
 * a stage the house never named (an auction staged before its stage was
 * un-configured).
 *
 * @param stage - The auction's derived closing stage (`deriveCloseStage`), 0–4.
 * @param labels - The house's labels.
 * @returns The display label.
 */
export function closeStageLabel(stage: number, labels: CloseStageLabelSource): string {
  if (stage <= 0) return houseClosedLabel(labels);
  return labels.stages[stage - 1] ?? `Closing stage ${stage}`;
}
